import React, { ReactElement, ReactNode } from 'react';
import { Box, Dialog, DialogProps, TableCell, TableRow, Typography, useMediaQuery, useTheme } from '@mui/material';

/**
 * Gradivni blokovi za telefon. Tabele ostaju tabele (paginacija, sortiranje, kolapsibilni redovi),
 * ali na uskom ekranu svaki red se crta kao kartica preko cele širine umesto niza uskih kolona.
 */

/** true ispod `sm` breakpoint-a (telefon u portretu). */
export function useIsMobile(): boolean {
    const theme = useTheme();
    return useMediaQuery(theme.breakpoints.down('sm'));
}

/** Za `<TableHead sx={mobileHiddenHeadSx}>`: zaglavlje se sakriva kad se redovi crtaju kao kartice. */
export const mobileHiddenHeadSx = { display: { xs: 'none', sm: 'table-header-group' } } as const;

/** Za `<Table sx={mobileTableSx(650)}>`: desktop minWidth se ne primenjuje na telefonu. */
export function mobileTableSx(desktopMinWidth: number) {
    return { minWidth: { xs: 0, sm: desktopMinWidth } } as const;
}

export type MobileField = { label: ReactNode; value: ReactNode };

type MobileCardRowProps = {
    /** Red čipova iznad naslova (tip, status …). */
    header?: ReactNode;
    title?: ReactNode;
    subtitle?: ReactNode;
    /** Parovi labela / vrednost, u dve kolone. */
    fields?: MobileField[];
    /** Gornji desni ugao: meni ili ikonice. Klik ovde ne okida `onClick` kartice. */
    actions?: ReactNode;
    children?: ReactNode;
    onClick?: () => void;
    highlighted?: boolean;
};

/** Jedan red tabele prikazan kao kartica. Renderuje `<TableRow>`, pa ide direktno u `<TableBody>`. */
export function MobileCardRow(props: MobileCardRowProps): ReactElement {
    const { header, title, subtitle, fields, actions, children, onClick, highlighted } = props;

    return (
        <TableRow>
            <TableCell colSpan={99} sx={{ p: 1, borderBottom: 'none' }}>
                <Box
                    onClick={onClick}
                    sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        p: 1.5,
                        bgcolor: highlighted ? '#f5f5f5' : 'background.paper',
                        cursor: onClick ? 'pointer' : 'default',
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                            {header && (
                                <Box
                                    sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: title || subtitle ? 1 : 0 }}
                                >
                                    {header}
                                </Box>
                            )}
                            {title && (
                                <Typography
                                    variant="subtitle1"
                                    sx={{ fontWeight: 600, lineHeight: 1.3, wordBreak: 'break-word' }}
                                >
                                    {title}
                                </Typography>
                            )}
                            {subtitle && (
                                <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                                    {subtitle}
                                </Typography>
                            )}
                        </Box>
                        {actions && (
                            <Box
                                onClick={(e) => e.stopPropagation()}
                                sx={{ flexShrink: 0, ml: 'auto', mr: -1, mt: -1 }}
                            >
                                {actions}
                            </Box>
                        )}
                    </Box>
                    {fields && fields.length > 0 && (
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: 'auto 1fr',
                                columnGap: 1.5,
                                rowGap: 0.25,
                                mt: 1,
                            }}
                        >
                            {fields.map((field, index) => (
                                <React.Fragment key={index}>
                                    <Typography variant="caption" color="text.secondary" sx={{ pt: 0.4 }}>
                                        {field.label}
                                    </Typography>
                                    <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                                        {field.value}
                                    </Typography>
                                </React.Fragment>
                            ))}
                        </Box>
                    )}
                    {children && <Box sx={{ mt: 1 }}>{children}</Box>}
                </Box>
            </TableCell>
        </TableRow>
    );
}

/**
 * MUI `Dialog` koji je na telefonu preko celog ekrana (osim ako je `fullScreen` već zadat),
 * da forme ne plivaju u malom prozoru sa dvostrukim skrolom.
 */
export function ResponsiveDialog(props: DialogProps): ReactElement {
    const isMobile = useIsMobile();
    const { fullScreen, ...rest } = props;

    return <Dialog fullScreen={fullScreen ?? isMobile} {...rest} />;
}
